import { CurrencyAmount, JSBI, Token, Trade } from '@uniswap/sdk'
import React, { useCallback, useContext, useEffect, useMemo, useState, useRef } from 'react'
import { ArrowDown } from 'react-feather'
import ReactGA from 'react-ga'
import { Text } from 'rebass'
import { ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'
import AddressInputPanel from '../../components/AddressInputPanel'
import { ButtonError, ButtonLight, ButtonPrimary, ButtonConfirmed } from '../../components/Button'
import Card, { GreyCard } from '../../components/Card'
import { AutoColumn } from '../../components/Column'
import ConfirmSwapModal from '../../components/swap/ConfirmSwapModal'
import CurrencyInputPanel from '../../components/CurrencyInputPanel'
import { SwapPoolTabs } from '../../components/NavigationTabs'
import { AutoRow, RowBetween } from '../../components/Row'
import AdvancedSwapDetailsDropdown from '../../components/swap/AdvancedSwapDetailsDropdown'
import BetterTradeLink from '../../components/swap/BetterTradeLink'
import confirmPriceImpactWithoutFee from '../../components/swap/confirmPriceImpactWithoutFee'
import { ArrowWrapper, BottomGrouping, SwapCallbackError, Wrapper } from '../../components/swap/styleds'
import TradePrice from '../../components/swap/TradePrice'
import TokenRiskWarning from '../../components/swap/TokenRiskWarning'
import TokenWarningModal from '../../components/TokenWarningModal'
import ProgressSteps from '../../components/ProgressSteps'

import { BETTER_TRADE_LINK_THRESHOLD, INITIAL_ALLOWED_SLIPPAGE } from '../../constants'
import { getTradeVersion, isTradeBetter } from '../../data/V1'
import { useActiveWeb3React } from '../../hooks'
import { useCurrency } from '../../hooks/Tokens'
import { ApprovalState, useApproveCallbackFromTrade } from '../../hooks/useApproveCallback'
import useENSAddress from '../../hooks/useENSAddress'
import { useSwapCallback } from '../../hooks/useSwapCallback'
import useToggledVersion, { Version } from '../../hooks/useToggledVersion'
import useWrapCallback, { WrapType } from '../../hooks/useWrapCallback'
import { getTaxAdjustedAmounts, getTaxAdjustedExecutionPrice } from '../../hooks/Trades'
import { isTaxToken, getTotalSellTax, getTotalBuyTax } from '../../utils/dynamicTaxToken'
import { useTaxTokenDetection } from '../../hooks/useTaxTokenDetection'
import { useToggleSettingsMenu, useWalletModalToggle } from '../../state/application/hooks'
import { Field } from '../../state/swap/actions'
import {
  useDefaultsFromURLSearch,
  useDerivedSwapInfo,
  useSwapActionHandlers,
  useSwapState
} from '../../state/swap/hooks'
import { useExpertModeManager, useUserDeadline, useUserSlippageTolerance } from '../../state/user/hooks'
import { LinkStyledButton, TYPE } from '../../theme'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import { computeTradePriceBreakdown, warningSeverity } from '../../utils/prices'
import AppBody from '../AppBody'
import { ClickableText } from '../Pool/styleds'
import Loader from '../../components/Loader'
import { usePauseSwapStatus } from '../../state/wallet/hooks'
// import imgK from '../../assets/images/k.png'

export default function Swap() {
  const { t } = useTranslation()
  const loadedUrlParams = useDefaultsFromURLSearch()

  // token warning stuff
  const [loadedInputCurrency, loadedOutputCurrency] = [
    useCurrency(loadedUrlParams?.inputCurrencyId),
    useCurrency(loadedUrlParams?.outputCurrencyId)
  ]
  // const [dismissTokenWarning, setDismissTokenWarning] = useState<boolean>(false)
  const urlLoadedTokens: Token[] = useMemo(
    () => [loadedInputCurrency, loadedOutputCurrency]?.filter((c): c is Token => c instanceof Token) ?? [],
    [loadedInputCurrency, loadedOutputCurrency]
  )
  const handleConfirmTokenWarning = useCallback(() => {
    // setDismissTokenWarning(true)
  }, [])

  const { account } = useActiveWeb3React()
  const theme = useContext(ThemeContext)

  // toggle wallet when disconnected
  const toggleWalletModal = useWalletModalToggle()

  // for expert mode
  const toggleSettings = useToggleSettingsMenu()
  const [isExpertMode] = useExpertModeManager()

  // get custom setting values for user
  const [deadline] = useUserDeadline()
  const [userSlippage] = useUserSlippageTolerance()

  // swap state
  const { independentField, typedValue, recipient } = useSwapState()
  const {
    v1Trade,
    v2Trade,
    currencyBalances,
    parsedAmount,
    currencies,
    inputError: swapInputError
  } = useDerivedSwapInfo()
  const { wrapType, execute: onWrap, inputError: wrapInputError } = useWrapCallback(
    currencies[Field.INPUT],
    currencies[Field.OUTPUT],
    typedValue
  )
  const showWrap: boolean = wrapType !== WrapType.NOT_APPLICABLE
  const { address: recipientAddress } = useENSAddress(recipient)
  const toggledVersion = useToggledVersion()
  const trade = showWrap
    ? undefined
    : {
        [Version.v1]: v1Trade,
        [Version.v2]: v2Trade
      }[toggledVersion]

  // Detect tax tokens automatically when currencies change
  const inputToken = currencies[Field.INPUT] instanceof Token ? (currencies[Field.INPUT] as Token) : undefined
  const outputToken = currencies[Field.OUTPUT] instanceof Token ? (currencies[Field.OUTPUT] as Token) : undefined
  useTaxTokenDetection(inputToken, outputToken)

  const betterTradeLinkVersion: Version | undefined =
    toggledVersion === Version.v2 && isTradeBetter(v2Trade, v1Trade, BETTER_TRADE_LINK_THRESHOLD)
      ? Version.v1
      : toggledVersion === Version.v1 && isTradeBetter(v1Trade, v2Trade)
      ? Version.v2
      : undefined

  const parsedAmounts = showWrap
    ? {
        [Field.INPUT]: parsedAmount,
        [Field.OUTPUT]: parsedAmount
      }
    : {
        [Field.INPUT]: independentField === Field.INPUT ? parsedAmount : trade?.inputAmount,
        [Field.OUTPUT]: independentField === Field.OUTPUT ? parsedAmount : trade?.outputAmount
      }

  const { onSwitchTokens, onCurrencySelection, onUserInput, onChangeRecipient } = useSwapActionHandlers()
  const isValid = !swapInputError
  const dependentField: Field = independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT

  const handleTypeInput = useCallback(
    (value: string) => {
      onUserInput(Field.INPUT, value)
    },
    [onUserInput]
  )
  const handleTypeOutput = useCallback(
    (value: string) => {
      onUserInput(Field.OUTPUT, value)
    },
    [onUserInput]
  )

  // modal and loading
  const [{ showConfirm, tradeToConfirm, swapErrorMessage, attemptingTxn, txHash }, setSwapState] = useState<{
    showConfirm: boolean
    tradeToConfirm: Trade | undefined
    attemptingTxn: boolean
    swapErrorMessage: string | undefined
    txHash: string | undefined
  }>({
    showConfirm: false,
    tradeToConfirm: undefined,
    attemptingTxn: false,
    swapErrorMessage: undefined,
    txHash: undefined
  })

  // Calculate tax-adjusted amounts for tax tokens
  const taxAdjustedAmounts = useMemo(() => {
    if (!trade || showWrap) return { adjustedInputAmount: null, adjustedOutputAmount: null }

    // Log for debugging
    console.log('Calculating tax-adjusted amounts', {
      inputToken: inputToken?.symbol,
      outputToken: outputToken?.symbol,
      independentField,
      dependentField,
      typedValue,
      trade: trade
        ? `${trade.inputAmount.toSignificant(6)} ${
            trade.inputAmount.currency.symbol
          } -> ${trade.outputAmount.toSignificant(6)} ${trade.outputAmount.currency.symbol}`
        : null
    })

    const result = getTaxAdjustedAmounts(trade)
    console.log('Tax adjustment result:', {
      adjustedInput: result.adjustedInputAmount?.toSignificant(6),
      adjustedOutput: result.adjustedOutputAmount?.toSignificant(6)
    })

    return result
  }, [trade, showWrap, inputToken, outputToken, independentField, dependentField, typedValue])

  // Calculate tax-adjusted execution price
  const taxAdjustedExecutionPrice = useMemo(() => {
    return getTaxAdjustedExecutionPrice(trade || null)
  }, [trade])

  // Calculate tax-aware slippage for tax tokens
  const allowedSlippage = useMemo(() => {
    if (!trade) return userSlippage

    const inputToken = trade.inputAmount.currency instanceof Token ? trade.inputAmount.currency : undefined
    const outputToken = trade.outputAmount.currency instanceof Token ? trade.outputAmount.currency : undefined

    let additionalSlippage = 0

    // Add tax percentage as additional slippage
    if (inputToken && isTaxToken(inputToken)) {
      const sellTax = getTotalSellTax(inputToken)
      additionalSlippage += parseFloat(sellTax.toFixed(2))
    }

    if (outputToken && isTaxToken(outputToken)) {
      const buyTax = getTotalBuyTax(outputToken)
      additionalSlippage += parseFloat(buyTax.toFixed(2))
    }

    // Convert percentage to basis points and add to user slippage
    // Add extra safety margin for tax tokens (additional 10% to account for price impact and MEV)
    const extraMargin = additionalSlippage > 0 ? 1000 : 0 // 10% extra margin for tax tokens
    const finalSlippage = userSlippage + Math.floor(additionalSlippage * 100) + extraMargin

    // console.log('Slippage calculation:', {
    //   userSlippage,
    //   additionalSlippage,
    //   extraMargin,
    //   finalSlippage,
    //   inputToken: inputToken?.symbol,
    //   outputToken: outputToken?.symbol
    // })

    return finalSlippage
  }, [trade, userSlippage])

  const formattedAmounts = {
    [independentField]: typedValue,
    [dependentField]: showWrap
      ? parsedAmounts[independentField]?.toExact() ?? ''
      : (() => {
          // Always try to use tax-adjusted amounts if available
          const adjustedOutput = taxAdjustedAmounts.adjustedOutputAmount
          const adjustedInput = taxAdjustedAmounts.adjustedInputAmount

          // console.log('FormattedAmounts logic:', {
          //   dependentField,
          //   showWrap,
          //   hasAdjustedOutput: !!adjustedOutput,
          //   hasAdjustedInput: !!adjustedInput,
          //   originalParsedAmount: parsedAmounts[dependentField]?.toSignificant(6)
          // })

          // For output field, always use adjusted output if available
          if (dependentField === Field.OUTPUT && adjustedOutput) {
            console.log('Using adjusted output:', adjustedOutput.toSignificant(6))
            return adjustedOutput.toSignificant(6)
          }

          // For input field, use adjusted input if available (for exact output trades)
          if (dependentField === Field.INPUT && adjustedInput) {
            console.log('Using adjusted input:', adjustedInput.toSignificant(6))
            return adjustedInput.toSignificant(6)
          }

          // Fallback to original parsed amount
          const fallback = parsedAmounts[dependentField]?.toSignificant(6) ?? ''
          // console.log('Using fallback:', fallback)
          return fallback
        })()
  }

  const route = trade?.route
  const userHasSpecifiedInputOutput = Boolean(
    currencies[Field.INPUT] && currencies[Field.OUTPUT] && parsedAmounts[independentField]?.greaterThan(JSBI.BigInt(0))
  )
  const noRoute = !route

  // check whether the user has approved the router on the input token
  const [approval, approveCallback] = useApproveCallbackFromTrade(trade, allowedSlippage)

  // check if user has gone through approval process, used to show two step buttons, reset on token change
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)

  // mark when a user has submitted an approval, reset onTokenSelection for input field
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(currencyBalances[Field.INPUT])
  const atMaxAmountInput = Boolean(maxAmountInput && parsedAmounts[Field.INPUT]?.equalTo(maxAmountInput))

  // the callback to execute the swap
  const { callback: swapCallback, error: swapCallbackError } = useSwapCallback(
    trade,
    allowedSlippage,
    deadline,
    recipient
  )

  const { priceImpactWithoutFee } = computeTradePriceBreakdown(trade)

  const handleSwap = useCallback(() => {
    if (priceImpactWithoutFee && !confirmPriceImpactWithoutFee(priceImpactWithoutFee)) {
      return
    }
    if (!swapCallback) {
      return
    }
    setSwapState({ attemptingTxn: true, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: undefined })
    swapCallback()
      .then(hash => {
        setSwapState({ attemptingTxn: false, tradeToConfirm, showConfirm, swapErrorMessage: undefined, txHash: hash })

        ReactGA.event({
          category: 'Swap',
          action:
            recipient === null
              ? 'Swap w/o Send'
              : (recipientAddress ?? recipient) === account
              ? 'Swap w/o Send + recipient'
              : 'Swap w/ Send',
          label: [
            trade?.inputAmount?.currency?.symbol,
            trade?.outputAmount?.currency?.symbol,
            getTradeVersion(trade)
          ].join('/')
        })
      })
      .catch(error => {
        console.log({ error })
        setSwapState({
          attemptingTxn: false,
          tradeToConfirm,
          showConfirm,
          swapErrorMessage: error.message,
          txHash: undefined
        })
      })
  }, [tradeToConfirm, account, priceImpactWithoutFee, recipient, recipientAddress, showConfirm, swapCallback, trade])

  // errors
  const [showInverted, setShowInverted] = useState<boolean>(false)

  // warnings on slippage
  const priceImpactSeverity = warningSeverity(priceImpactWithoutFee)

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  // never show if price impact is above threshold in non expert mode
  const showApproveFlow =
    !swapInputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  const handleConfirmDismiss = useCallback(() => {
    setSwapState({ showConfirm: false, tradeToConfirm, attemptingTxn, swapErrorMessage, txHash })
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput(Field.INPUT, '')
    }
  }, [attemptingTxn, onUserInput, swapErrorMessage, tradeToConfirm, txHash])

  const handleAcceptChanges = useCallback(() => {
    setSwapState({ tradeToConfirm: trade, swapErrorMessage, txHash, attemptingTxn, showConfirm })
  }, [attemptingTxn, showConfirm, swapErrorMessage, trade, txHash])

  const handleInputSelect = useCallback(
    inputCurrency => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      onCurrencySelection(Field.INPUT, inputCurrency)
    },
    [onCurrencySelection]
  )

  const handleMaxInput = useCallback(() => {
    maxAmountInput && onUserInput(Field.INPUT, maxAmountInput.toExact())
  }, [maxAmountInput, onUserInput])

  const handleOutputSelect = useCallback(outputCurrency => onCurrencySelection(Field.OUTPUT, outputCurrency), [
    onCurrencySelection
  ])

  // 获取兑换暂停状态
  const { isPaused: isSwapPaused } = usePauseSwapStatus()

  // 创建引用
  const leftButtonRef = useRef(null)
  const kLineButtonRef = useRef(null)

  // 添加监测和同步样式的效果
  useEffect(() => {
    if (leftButtonRef.current && kLineButtonRef.current) {
      const syncStyles = () => {
        const leftButton = (leftButtonRef.current as unknown) as HTMLButtonElement
        const kLineButton = (kLineButtonRef.current as unknown) as HTMLButtonElement

        if (leftButton && kLineButton) {
          const btnText = leftButton?.getElementsByTagName('button')?.[0]?.getElementsByTagName('div')?.[0]
          if (btnText) {
            const btnStyle = window.getComputedStyle(btnText)
            kLineButton.style.fontSize = btnStyle.fontSize
          }
          // 获取左侧按钮的计算样式
          const leftButtonStyle = window.getComputedStyle(leftButton)

          // 同步字体大小和高度
          kLineButton.style.height = leftButtonStyle.height
          kLineButton.style.padding = leftButtonStyle.padding
        }
      }

      // 初始同步
      syncStyles()

      // 创建一个观察器来监视左侧按钮的变化
      const observer = new MutationObserver(syncStyles)

      // 配置观察器
      observer.observe(leftButtonRef.current as any, {
        attributes: true,
        childList: true,
        subtree: true
      })

      // 清理函数
      return () => observer.disconnect()
    }
    return
  }, [leftButtonRef, kLineButtonRef])

  return (
    <>
      <TokenWarningModal isOpen={false} tokens={urlLoadedTokens} onConfirm={handleConfirmTokenWarning} />
      <AppBody>
        <SwapPoolTabs active={'swap'} />
        <Wrapper id="swap-page">
          <ConfirmSwapModal
            isOpen={showConfirm}
            trade={trade}
            originalTrade={tradeToConfirm}
            onAcceptChanges={handleAcceptChanges}
            attemptingTxn={attemptingTxn}
            txHash={txHash}
            recipient={recipient}
            allowedSlippage={allowedSlippage}
            onConfirm={handleSwap}
            swapErrorMessage={swapErrorMessage}
            onDismiss={handleConfirmDismiss}
          />

          <AutoColumn gap={'md'}>
            <CurrencyInputPanel
              label={independentField === Field.OUTPUT && !showWrap && trade ? t('payAmountEstimated') : t('payAmount')}
              value={formattedAmounts[Field.INPUT]}
              showMaxButton={!atMaxAmountInput}
              currency={currencies[Field.INPUT]}
              onUserInput={handleTypeInput}
              onMax={handleMaxInput}
              onCurrencySelect={handleInputSelect}
              otherCurrency={currencies[Field.OUTPUT]}
              id="swap-currency-input"
            />
            <AutoColumn justify="space-between">
              <AutoRow justify={isExpertMode ? 'space-between' : 'center'} style={{ padding: '0 1rem' }}>
                <ArrowWrapper clickable>
                  <ArrowDown
                    size="16"
                    onClick={() => {
                      setApprovalSubmitted(false) // reset 2 step UI for approvals
                      onSwitchTokens()

                      // Force recalculation by re-entering the typed value
                      // This ensures tax adjustments are applied after switching
                      if (typedValue && typedValue !== '0') {
                        setTimeout(() => {
                          onUserInput(independentField, typedValue)
                        }, 50)
                      }
                    }}
                    color={currencies[Field.INPUT] && currencies[Field.OUTPUT] ? theme.primary1 : theme.text2}
                  />
                </ArrowWrapper>
                {recipient === null && !showWrap && isExpertMode ? (
                  <LinkStyledButton id="add-recipient-button" onClick={() => onChangeRecipient('')}>
                    {t('addRecipient')}
                  </LinkStyledButton>
                ) : null}
              </AutoRow>
            </AutoColumn>
            <CurrencyInputPanel
              value={formattedAmounts[Field.OUTPUT]}
              onUserInput={handleTypeOutput}
              label={independentField === Field.INPUT && !showWrap && trade ? t('receiveAmountEstimated') : t('receiveAmount')}
              showMaxButton={false}
              currency={currencies[Field.OUTPUT]}
              onCurrencySelect={handleOutputSelect}
              otherCurrency={currencies[Field.INPUT]}
              id="swap-currency-output"
            />

            {recipient !== null && !showWrap ? (
              <>
                <AutoRow justify="space-between" style={{ padding: '0 1rem' }}>
                  <ArrowWrapper clickable={false}>
                    <ArrowDown size="16" color={theme.text2} />
                  </ArrowWrapper>
                  <LinkStyledButton id="remove-recipient-button" onClick={() => onChangeRecipient(null)}>
                    {t('removeRecipient')}
                  </LinkStyledButton>
                </AutoRow>
                <AddressInputPanel id="recipient" value={recipient} onChange={onChangeRecipient} />
              </>
            ) : null}

            <TokenRiskWarning trade={trade} />

            {showWrap ? null : (
              <Card padding={'.25rem .75rem 0 .75rem'} borderRadius={'20px'}>
                <AutoColumn gap="4px">
                  {Boolean(trade) && (
                    <RowBetween align="center">
                      <Text fontWeight={500} fontSize={14} color={theme.text2}>
                        {t('price')}
                      </Text>
                      <TradePrice
                        price={taxAdjustedExecutionPrice}
                        showInverted={showInverted}
                        setShowInverted={setShowInverted}
                      />
                    </RowBetween>
                  )}
                  {allowedSlippage !== INITIAL_ALLOWED_SLIPPAGE && (
                    <RowBetween align="center">
                      <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                        {t('slippageTolerance')}
                      </ClickableText>
                      <ClickableText fontWeight={500} fontSize={14} color={theme.text2} onClick={toggleSettings}>
                        {allowedSlippage / 100}%
                      </ClickableText>
                    </RowBetween>
                  )}
                </AutoColumn>
              </Card>
            )}
          </AutoColumn>
          <BottomGrouping>
            {isSwapPaused && (
              <TYPE.main mb={'20px'} textAlign={'center'} color={theme.red1}>
                {t('swapPaused')}
              </TYPE.main>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '50%' }} ref={leftButtonRef}>
                {!account ? (
                  <ButtonLight onClick={toggleWalletModal}>{t('connectWallet')}</ButtonLight>
                ) : showWrap ? (
                  <ButtonPrimary disabled={Boolean(wrapInputError) || isSwapPaused} onClick={onWrap}>
                    {wrapInputError ??
                      (wrapType === WrapType.WRAP ? t('wrap') : wrapType === WrapType.UNWRAP ? t('unwrap') : null)}
                  </ButtonPrimary>
                ) : noRoute && userHasSpecifiedInputOutput ? (
                  <GreyCard style={{ textAlign: 'center' }}>
                    <TYPE.main mb="4px">{t('insufficientLiquidityForTrade')}</TYPE.main>
                  </GreyCard>
                ) : showApproveFlow ? (
                  <RowBetween>
                    <ButtonConfirmed
                      onClick={approveCallback}
                      disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted || isSwapPaused}
                      width="48%"
                      altDisabledStyle={approval === ApprovalState.PENDING} // show solid button while waiting
                      confirmed={approval === ApprovalState.APPROVED}
                    >
                      {approval === ApprovalState.PENDING ? (
                        <AutoRow gap="6px" justify="center">
                          {t('approvingLoader')} <Loader stroke="white" />
                        </AutoRow>
                      ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                        t('approved')
                      ) : (
                        t('approve') + ' ' + currencies[Field.INPUT]?.symbol
                      )}
                    </ButtonConfirmed>
                    <ButtonError
                      onClick={() => {
                        if (isExpertMode) {
                          handleSwap()
                        } else {
                          setSwapState({
                            tradeToConfirm: trade,
                            attemptingTxn: false,
                            swapErrorMessage: undefined,
                            showConfirm: true,
                            txHash: undefined
                          })
                        }
                      }}
                      width="48%"
                      id="swap-button"
                      disabled={
                        !isValid ||
                        approval !== ApprovalState.APPROVED ||
                        (priceImpactSeverity > 3 && !isExpertMode) ||
                        isSwapPaused
                      }
                      error={isValid && priceImpactSeverity > 2}
                    >
                      <Text fontSize={16} fontWeight={500}>
                        {priceImpactSeverity > 3 && !isExpertMode
                          ? t('priceImpactTooHigh')
                          : `${t('swap')}${priceImpactSeverity > 2 ? ' ' + t('swapAnyway') : ''}`}
                      </Text>
                    </ButtonError>
                  </RowBetween>
                ) : (
                  <ButtonError
                    onClick={() => {
                      if (isExpertMode) {
                        handleSwap()
                      } else {
                        setSwapState({
                          tradeToConfirm: trade,
                          attemptingTxn: false,
                          swapErrorMessage: undefined,
                          showConfirm: true,
                          txHash: undefined
                        })
                      }
                    }}
                    id="swap-button"
                    disabled={
                      !isValid || (priceImpactSeverity > 3 && !isExpertMode) || !!swapCallbackError || isSwapPaused
                    }
                    error={isValid && priceImpactSeverity > 2 && !swapCallbackError}
                  >
                    <Text fontSize={20} fontWeight={500}>
                      {swapInputError
                        ? swapInputError
                        : priceImpactSeverity > 3 && !isExpertMode
                        ? t('priceImpactExtremely')
                        : isSwapPaused
                        ? t('swapPausedMessage')
                        : `${t('swap')}${priceImpactSeverity > 2 ? ' ' + t('swapAnyway') : ''}`}
                    </Text>
                  </ButtonError>
                )}
              </div>
              <ButtonLight
                ref={kLineButtonRef}
                style={{
                  width: '50%'
                }}
                onClick={() => {
                  // 检查是否有特定代币地址，如果有则使用特定链接，否则使用默认链接
                  const specificTokenAddress = '0x62f41ac5c3cd18ad0f9c15e36a7edc5410fca053'
                  const inputAddress = inputToken?.address?.toLowerCase()
                  const outputAddress = outputToken?.address?.toLowerCase()
                  let tokenAddress = '0x82f9d23cb62ec0016109b7c4b8db34890fdba0f0'

                  if (inputAddress === specificTokenAddress || outputAddress === specificTokenAddress) {
                    tokenAddress = specificTokenAddress
                  }
                  // 其他所有情况都使用默认链接
                  window.open(`https://m.ave.ai/token/${tokenAddress}-mud?utm_source=mudrealm`, '_blank')
                }}
              >
                {t('klineChart')}
              </ButtonLight>
            </div>
            {showApproveFlow && <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />}
            {isExpertMode && swapErrorMessage ? <SwapCallbackError error={swapErrorMessage} /> : null}
            {betterTradeLinkVersion && <BetterTradeLink version={betterTradeLinkVersion} />}
          </BottomGrouping>
        </Wrapper>
      </AppBody>
      <AdvancedSwapDetailsDropdown trade={trade} />
    </>
  )
}
